import React, { useState } from 'react';
import { SelectOption } from './Select';

export const useSelectState = (
  options: SelectOption[],
  isMulti: boolean,
  selectedOptions?: SelectOption[],
  selectedOption?: SelectOption,
) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isTyping, setIsTyping] = useState(false);

  const filteredOptions = isMulti
    ? options
        .filter((option) => {
          const labelText =
            typeof option.label === 'string' ? option.label : '';
          return labelText.toLowerCase().includes(searchTerm.toLowerCase());
        })
        .filter((option) => {
          return !(selectedOptions ?? []).some((o) =>
            typeof o.value === 'object' && typeof option.value === 'object'
              ? JSON.stringify(o.value) === JSON.stringify(option.value)
              : o.value === option.value,
          );
        })
    : options.filter((option) => {
        const labelText = typeof option.label === 'string' ? option.label : '';

        return typeof labelText === 'string'
          ? labelText
              .toLowerCase()
              .includes(
                typeof searchTerm === 'string' ? searchTerm.toLowerCase() : '',
              )
          : false;
      });

  const currentIndex = selectedOption
    ? filteredOptions.findIndex(
        (option) => option.label === selectedOption?.label,
      )
    : null;

  const [focusedIndex, setFocusedIndex] = useState<number>(currentIndex ?? -1);

  return {
    isOpen,
    setIsOpen,
    searchTerm,
    setSearchTerm,
    isTyping,
    setIsTyping,
    focusedIndex,
    setFocusedIndex,
    filteredOptions,
    currentIndex,
  };
};

export const useKeyControls = (
  focusedIndex: number,
  filteredOptions: SelectOption[],
  setFocusedIndex: React.Dispatch<React.SetStateAction<number>>,
  isOpen: boolean,
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>,
  handleOptionClick: (option: SelectOption) => void,
  setSelectedOption?: React.Dispatch<React.SetStateAction<SelectOption | null>>,
  setSelectedOptions?: React.Dispatch<
    React.SetStateAction<SelectOption[] | null>
  >,
  isMulti: boolean = false,
) => {
  const handleArrowDown = () => {
    const nextIndex =
      focusedIndex < filteredOptions.length - 1 ? focusedIndex + 1 : 0;
    setFocusedIndex(nextIndex);
    if (setSelectedOption) {
      setSelectedOption(filteredOptions[nextIndex]);
    } else if (setSelectedOptions) {
      setSelectedOptions((prev) => [
        ...(prev ?? []),
        filteredOptions[nextIndex],
      ]);
    }
  };

  const handleArrowUp = () => {
    const prevIndex =
      focusedIndex > 0 ? focusedIndex - 1 : filteredOptions.length - 1;
    setFocusedIndex(prevIndex);
    if (setSelectedOption) {
      setSelectedOption(filteredOptions[prevIndex]);
    } else if (setSelectedOptions) {
      setSelectedOptions((prev) => [
        ...(prev ?? []),
        filteredOptions[prevIndex],
      ]);
    }
  };

  const handleEnter = () => {
    if (focusedIndex >= 0 && focusedIndex < filteredOptions.length) {
      handleOptionClick(filteredOptions[focusedIndex]);
    } else {
      handleOptionClick(filteredOptions[0]);
    }
    if (isMulti) setIsOpen(true);
  };

  const handleEscape = () => {
    setIsOpen(false);
    setFocusedIndex(0);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        handleArrowDown();
        break;
      case 'ArrowUp':
        e.preventDefault();
        handleArrowUp();
        break;
      case 'Enter':
        e.preventDefault();
        handleEnter();
        break;
      case 'Escape':
        e.preventDefault();
        handleEscape();
        break;
      case 'Delete':
      case 'Backspace':
        e.stopPropagation();
        break;
      default:
        break;
    }
  };

  return { handleKeyDown };
};
